import downArrow from "@assets/actions/down-arrow.svg";
import ArticleCard from "@components/Blocks/Card/ArticleCard";
import Heading from "@elements/Typography/Heading";
import { ProcessedArticleData } from "@helper/transformer/useOsResponse";
import { useTMCustomEventAnalyticsCall } from "@hooks/useAnalyticsCall/useTMCustomEventAnalyticsCall";
import { useEffect, useRef, useState } from "react";
import { twMerge } from "tailwind-merge";

type RelatedArticleAccordonProps = Readonly<{
  testId?: string;
  rootClassName?: string;
  dataSectionName?: string;
  relatedArticles: ProcessedArticleData[];
  source?: string;
  middleArticle?: boolean;
  loadGaEvent?: boolean;
}>;

type WithGaEventLoad = {
  loadGaEvent: true;
  pageUrl: string;
};

type WithoutGaEventLoad = {
  loadGaEvent?: false | undefined;
  pageUrl?: string;
};

export type AccordonProps = RelatedArticleAccordonProps &
  (WithGaEventLoad | WithoutGaEventLoad);

export default function RelatedArticleAccordion({
  testId,
  rootClassName,
  dataSectionName,
  relatedArticles,
  source,
  middleArticle = false,
  loadGaEvent = false,
  pageUrl,
}: AccordonProps): React.ReactElement {
  const [isOpen, setIsOpen] = useState(false);
  const [visibleItems, setVisibleItems] = useState(2);

  const toggleVisibility = () => {
    setIsOpen(!isOpen);
    setVisibleItems(isOpen ? 2 : relatedArticles.length);
  };

  const TmCustomGaEvent = useTMCustomEventAnalyticsCall();
  const hasRun = useRef(false);
  useEffect(() => {
    if (loadGaEvent && !hasRun.current) {
      TmCustomGaEvent({
        event: "customEvent",
        eventCategory: middleArticle
          ? "related-story-middle"
          : "related-story-bottom",
        eventAction: "Visible",
        eventLabel: origin + pageUrl,
      });
      hasRun.current = true;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div
      className={rootClassName}
      data-testid={testId}
      data-section-name={dataSectionName}
    >
      <Heading
        Element="h6"
        className="font-primary font-bold text-md leading-250 text-gray-100"
      >
        தொடர்புடைய செய்திகள்
      </Heading>
      {relatedArticles.slice(0, visibleItems).map((article, i, row) => (
        <ArticleCard
          key={article.id}
          displayWidth={175}
          displayHeight={90}
          overrideStyle={{
            cardWrapper: twMerge(
              "w-full lg:w-full flex-row-reverse py-0 pb-2.5 gap-2.5",
              i + 1 === row.length && "border-0",
            ),
            titleStyle:
              "font-semibold font-primary text-sm2 text-gray-100 hover:text-gray-100",
            imageWrapperStyle: "w-40 max-w-44 lg:w-40 h-23 max-sm:w-23",
            imageStyle: "w-full h-23",
          }}
          refMedium={source}
          {...article}
          elements={{
            hasParagraph: false,
            hasDate: false,
          }}
        />
      ))}
      {relatedArticles.length > 2 && (
        <button
          onClick={toggleVisibility}
          className="w-full font-primary text-xs font-medium leading-230 text-center text-gray-100 focus:outline-none transition-all duration-300"
        >
          <span className="inline">Show {isOpen ? "Less" : "More"}</span>
          <img
            src={downArrow}
            alt={"dropdown-arrow"}
            width={10}
            height={10}
            className={twMerge(
              "ml-3 duration-700 inline-flex",
              isOpen && "-rotate-180",
            )}
          />
        </button>
      )}
      <div
        className={twMerge(
          "transition-all duration-500 ease-in-out overflow-hidden",
          isOpen ? "max-h-screen" : "max-h-0",
        )}
      >
        {isOpen &&
          relatedArticles.slice(visibleItems).map((article, i, row) => (
            <ArticleCard
              key={article.id}
              displayWidth={175}
              displayHeight={90}
              overrideStyle={{
                cardWrapper: twMerge(
                  "w-full lg:w-full flex-row-reverse py-0 pb-2.5 gap-2.5",
                  i + 1 === row.length && "border-0",
                ),
                titleStyle:
                  "font-semibold font-primary text-sm2 text-gray-100 hover:text-gray-100",
                imageWrapperStyle: "w-40 max-w-44 lg:w-40 h-23 max-sm:w-23",
                imageStyle: "w-full h-23",
              }}
              refMedium={source}
              {...article}
              elements={{
                hasParagraph: false,
                hasDate: false,
              }}
            />
          ))}
      </div>
    </div>
  );
}
